/**
 * The idempotent pricing requests that have been sent and may not have been answered (batch 8 T13b1 repair, #3034): a customer
 * price create, a campaign create, a campaign item add and a category recalculation apply.
 *
 * Each is a keyed command: the server binds the `Idempotency-Key` to the WHOLE request and answers a repeat from its record. That
 * only helps if the repeat is the same key with the same payload to the same target, so the exact request is written down BEFORE
 * it is sent and kept until the server has decided:
 *
 *  - accepted (2xx, also a replay): removed;
 *  - refused (a 4xx the server read and stored): removed, the user corrects it and a new request gets a new key;
 *  - uncertain (lost connection, timeout, 5xx, an unanswered earlier attempt): KEPT. The screen offers to send the identical request
 *    again, or to abandon it knowingly. It never sends the same intent under a fresh key, because if the first attempt committed that
 *    would create it twice.
 *
 * Ownership. An entry belongs to the session that wrote it (tenant, user, session family; never a token or the in-memory epoch) and
 * lives in a document named after that owner. Another tenant, another user, a new login of the same user or a signed-out page
 * cannot find it, overwrite it or delete it: it is inert and is only ever resent by its own session, when the user asks. A reload of
 * the same session finds it again with the exact key, payload, operation kind and target (a campaign item add keeps its campaign).
 * Removal is compare-and-delete: only the entry that still holds the same key goes, so an old answer never removes a newer intent.
 *
 * Storage is `sessionStorage` (per tab). The document is bounded (entries, size) and every entry is validated on read. A browser that
 * refuses storage is reported (`unavailable`): the entries then live in memory for this page only, and the screen says that a reload
 * cannot recover them. A document that cannot be read back is `corrupt`: nothing in it is used, no new keyed request starts until the
 * user knowingly discards the damaged record, because an unknown earlier request cannot be ruled out.
 *
 * Nothing here ever sends a request. Entries are only resent by an explicit action of the owner.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { ownerEquals, ownerId, type PricingOwner } from './pricingSession';

export type JournalKind = 'customer_price_create' | 'campaign_create' | 'campaign_item_add' | 'recalc_apply';
export type JournalStatus = 'ok' | 'unavailable' | 'corrupt';
export type PutStatus = 'stored' | 'memory_only' | 'conflict' | 'full' | 'corrupt';

export interface JournalRequest {
  /** The `Idempotency-Key`: fixed for the life of the operation. */
  key: string;
  /** The exact payload. Never rebuilt, never edited. */
  body: unknown;
}

export interface JournalEntry {
  /** `ephemeral`: held in memory for one hook that has no journal slot. */
  readonly kind: JournalKind | 'ephemeral';
  /** The address within the owner's journal. */
  readonly slot: string;
  /** A campaign id (item add) or a category id (apply); `null` for the kinds that have no target. */
  readonly target: string | null;
  readonly owner: PricingOwner;
  readonly request: JournalRequest;
  readonly startedAt: string;
  /** How many times this very request was sent. */
  readonly attempts: number;
  /** In-memory generation it was sent under; `null` after a reload (the epoch is not persisted). */
  readonly epoch: number | null;
  /** False: held in memory only (a keyed request without a journal slot). */
  readonly persisted: boolean;
}

export interface JournalView {
  readonly status: JournalStatus;
  readonly entries: readonly JournalEntry[];
  /** Slots with a request on its way in this page (possibly started by a screen that has since unmounted). */
  readonly inFlight: ReadonlySet<string>;
}

export const MAX_JOURNAL_ENTRIES = 20;
const MAX_DOCUMENT_CHARS = 256 * 1024;
const PREFIX = 'os-buddy:part-pricing-journal:v1:';

/** The address of an entry within its owner's journal. A recalculation apply is one slot per owner. */
export function slotOf(kind: JournalKind, target: string | null): string {
  return kind === 'campaign_item_add' ? `${kind}:${target ?? ''}` : kind;
}

export const EMPTY_VIEW: JournalView = Object.freeze({ status: 'ok', entries: [], inFlight: new Set<string>() });

// ---------------------------------------------------------------------------
// Validation of what is read back
// ---------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown, max: number): value is string => typeof value === 'string' && value.length > 0 && value.length <= max;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DECIMAL = /^\d{1,12}(\.\d{1,4})?$/;
const KINDS: readonly JournalKind[] = ['customer_price_create', 'campaign_create', 'campaign_item_add', 'recalc_apply'];
const PRICE_TYPES = ['fixed_price', 'discount_percent'];

const onlyKeys = (value: Record<string, unknown>, allowed: readonly string[]): boolean => Object.keys(value).every((key) => allowed.includes(key));
const exactlyOne = (value: Record<string, unknown>, a: string, b: string): boolean => (value[a] !== undefined) !== (value[b] !== undefined);
const optionalDate = (value: unknown): boolean => value === undefined || value === null || (typeof value === 'string' && DATE.test(value));

function validTargetBody(value: Record<string, unknown>): boolean {
  return (
    exactlyOne(value, 'partId', 'categoryId') &&
    (value['partId'] === undefined || isText(value['partId'], 100)) &&
    (value['categoryId'] === undefined || isText(value['categoryId'], 100))
  );
}

const validators: Record<JournalKind, (body: unknown) => boolean> = {
  customer_price_create: (body) =>
    isRecord(body) &&
    onlyKeys(body, ['companyId', 'personId', 'partId', 'categoryId', 'priceType', 'value', 'validFrom', 'validTo', 'isActive', 'note']) &&
    exactlyOne(body, 'companyId', 'personId') &&
    (body['companyId'] === undefined || isText(body['companyId'], 100)) &&
    (body['personId'] === undefined || isText(body['personId'], 100)) &&
    validTargetBody(body) &&
    typeof body['priceType'] === 'string' &&
    PRICE_TYPES.includes(body['priceType']) &&
    typeof body['value'] === 'string' &&
    DECIMAL.test(body['value']) &&
    optionalDate(body['validFrom']) &&
    optionalDate(body['validTo']) &&
    (body['isActive'] === undefined || typeof body['isActive'] === 'boolean') &&
    (body['note'] === undefined || body['note'] === null || (typeof body['note'] === 'string' && body['note'].length <= 500)),
  campaign_create: (body) =>
    isRecord(body) &&
    onlyKeys(body, ['name', 'startsOn', 'endsOn', 'isActive']) &&
    isText(body['name'], 100) &&
    typeof body['startsOn'] === 'string' &&
    DATE.test(body['startsOn']) &&
    typeof body['endsOn'] === 'string' &&
    DATE.test(body['endsOn']) &&
    (body['isActive'] === undefined || typeof body['isActive'] === 'boolean'),
  campaign_item_add: (body) =>
    isRecord(body) &&
    onlyKeys(body, ['partId', 'categoryId', 'priceType', 'value']) &&
    validTargetBody(body) &&
    typeof body['priceType'] === 'string' &&
    PRICE_TYPES.includes(body['priceType']) &&
    typeof body['value'] === 'string' &&
    DECIMAL.test(body['value']),
  recalc_apply: (body) => {
    if (!isRecord(body) || !onlyKeys(body, ['categoryId', 'partIds', 'previewToken'])) return false;
    const ids = body['partIds'];
    if (!isText(body['categoryId'], 100) || !isText(body['previewToken'], 200)) return false;
    return Array.isArray(ids) && ids.length > 0 && ids.length <= 200 && ids.every((id) => isText(id, 100)) && new Set(ids as string[]).size === ids.length;
  },
};

function validTarget(kind: JournalKind, target: unknown, body: unknown): boolean {
  if (kind === 'campaign_item_add') return isText(target, 100);
  if (kind === 'recalc_apply') return isText(target, 100) && isRecord(body) && body['categoryId'] === target;
  return target === null;
}

/** An entry as persisted. The owner is the document's, never the entry's own claim. */
interface StoredEntry {
  kind: JournalKind;
  target: string | null;
  key: string;
  body: unknown;
  startedAt: string;
  attempts: number;
}

function parseDocument(raw: string, owner: PricingOwner): StoredEntry[] | null {
  if (raw.length > MAX_DOCUMENT_CHARS) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed['v'] !== 1 || !isRecord(parsed['owner'])) return null;
  const claimed = parsed['owner'];
  if (!ownerEquals(owner, { tenantId: String(claimed['tenantId']), userId: String(claimed['userId']), sessionFamilyId: String(claimed['sessionFamilyId']) })) return null;
  const entries = parsed['entries'];
  if (!Array.isArray(entries) || entries.length > MAX_JOURNAL_ENTRIES) return null;
  const slots = new Set<string>();
  const result: StoredEntry[] = [];
  for (const entry of entries as unknown[]) {
    if (!isRecord(entry)) return null;
    const { kind, target, key, body, startedAt, attempts } = entry;
    if (typeof kind !== 'string' || !(KINDS as readonly string[]).includes(kind)) return null;
    const typedKind = kind as JournalKind;
    if (!isText(key, 200) || typeof startedAt !== 'string' || Number.isNaN(Date.parse(startedAt))) return null;
    if (typeof attempts !== 'number' || !Number.isSafeInteger(attempts) || attempts < 1 || attempts > 1000) return null;
    if (!validators[typedKind](body) || !validTarget(typedKind, target, body)) return null;
    const slot = slotOf(typedKind, target as string | null);
    if (slots.has(slot)) return null;
    slots.add(slot);
    result.push({ kind: typedKind, target: target as string | null, key, body, startedAt, attempts });
  }
  return result;
}

// ---------------------------------------------------------------------------
// The books: one per owner, in memory, backed by sessionStorage
// ---------------------------------------------------------------------------

interface Book {
  persisted: Map<string, JournalEntry>;
  /** Requests held without a journal slot (never written to storage). */
  ephemeral: Map<string, JournalEntry>;
  inFlight: Map<string, string>;
  /** What storage held when this book last agreed with it. `undefined`: never read. */
  lastRaw: string | null | undefined;
  unreadable: boolean;
  writeFailed: boolean;
  corrupt: boolean;
  view: JournalView | null;
}

const books = new Map<string, Book>();
const listeners = new Set<() => void>();

const addressOf = (owner: PricingOwner): string => `${PREFIX}${[owner.tenantId, owner.userId, owner.sessionFamilyId].map(encodeURIComponent).join(':')}`;

function notify(): void {
  for (const listener of [...listeners]) listener();
}

function bookOf(owner: PricingOwner): Book {
  const id = ownerId(owner);
  let book = books.get(id);
  if (!book) {
    book = {
      persisted: new Map(),
      ephemeral: new Map(),
      inFlight: new Map(),
      lastRaw: undefined,
      unreadable: false,
      writeFailed: false,
      corrupt: false,
      view: null,
    };
    books.set(id, book);
  }
  return book;
}

/** Brings the book in line with storage when storage changed under it (another script, a cleared store). */
function refresh(owner: PricingOwner, book: Book): void {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(addressOf(owner));
  } catch {
    if (!book.unreadable) {
      book.unreadable = true;
      book.view = null;
    }
    return;
  }
  const wasUnreadable = book.unreadable;
  if (wasUnreadable) {
    book.unreadable = false;
    book.view = null;
  }
  // Entries that only this page holds (storage was refused) are written back once storage works again, never dropped.
  if (raw === null && book.persisted.size > 0 && (wasUnreadable || book.writeFailed || book.lastRaw === undefined)) {
    write(owner, book);
    return;
  }
  if (book.lastRaw === raw) return;
  book.lastRaw = raw;
  book.view = null;
  book.writeFailed = false;
  if (raw === null) {
    book.persisted = new Map();
    book.corrupt = false;
    return;
  }
  const stored = parseDocument(raw, owner);
  if (stored === null) {
    book.persisted = new Map();
    book.corrupt = true;
    return;
  }
  book.corrupt = false;
  const previous = book.persisted;
  const next = new Map<string, JournalEntry>();
  for (const entry of stored) {
    const slot = slotOf(entry.kind, entry.target);
    const known = previous.get(slot);
    // The request object of a key this page already holds stays the same object.
    const request = known && known.request.key === entry.key ? known.request : { key: entry.key, body: entry.body };
    next.set(slot, {
      kind: entry.kind,
      slot,
      target: entry.target,
      owner,
      request,
      startedAt: entry.startedAt,
      attempts: entry.attempts,
      epoch: known && known.request.key === entry.key ? known.epoch : null,
      persisted: true,
    });
  }
  book.persisted = next;
}

/** Writes the persisted entries. Returns false (and remembers it) when the browser would not keep them. */
function write(owner: PricingOwner, book: Book): boolean {
  const address = addressOf(owner);
  const before = `${book.writeFailed}:${book.unreadable}`;
  try {
    if (book.persisted.size === 0) {
      window.sessionStorage.removeItem(address);
      book.lastRaw = null;
    } else {
      const text = JSON.stringify({
        v: 1,
        owner: { tenantId: owner.tenantId, userId: owner.userId, sessionFamilyId: owner.sessionFamilyId },
        entries: [...book.persisted.values()].map((entry) => ({
          kind: entry.kind,
          target: entry.target,
          key: entry.request.key,
          body: entry.request.body,
          startedAt: entry.startedAt,
          attempts: entry.attempts,
        })),
      });
      if (text.length > MAX_DOCUMENT_CHARS) throw new Error('journal document too large');
      window.sessionStorage.setItem(address, text);
      if (window.sessionStorage.getItem(address) !== text) throw new Error('journal document not kept');
      book.lastRaw = text;
    }
    book.writeFailed = false;
    return true;
  } catch {
    book.writeFailed = true;
    try {
      book.lastRaw = window.sessionStorage.getItem(address);
    } catch {
      book.unreadable = true;
    }
    return false;
  } finally {
    // The view only changes when the storage state does; callers that change entries invalidate it themselves. A write that
    // fails again must not make every snapshot a new object.
    if (before !== `${book.writeFailed}:${book.unreadable}`) book.view = null;
  }
}

export function getJournalView(owner: PricingOwner | null): JournalView {
  if (owner === null) return EMPTY_VIEW;
  const book = bookOf(owner);
  refresh(owner, book);
  if (book.view === null) {
    const status: JournalStatus = book.corrupt ? 'corrupt' : book.unreadable || book.writeFailed ? 'unavailable' : 'ok';
    book.view = {
      status,
      entries: [...book.persisted.values(), ...book.ephemeral.values()],
      inFlight: new Set(book.inFlight.keys()),
    };
  }
  return book.view;
}

export function subscribeJournal(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The journal of `owner`, live: re-renders when an entry, a flight or the storage state changes. */
export function useJournalView(owner: PricingOwner | null): JournalView {
  const snapshot = useCallback(() => getJournalView(owner), [owner]);
  return useSyncExternalStore(subscribeJournal, snapshot, snapshot);
}

export interface NewEntry {
  kind: JournalKind | 'ephemeral';
  target: string | null;
  request: JournalRequest;
  epoch: number;
  /** False: held in memory only, never written to storage and not counted against the bound. */
  persist: boolean;
  /** The slot: `slotOf(kind, target)` for a journal kind, a private one for a memory-only entry. */
  slot: string;
}

/** Writes the request down BEFORE it is sent. Never overwrites a different unresolved request of the same slot. */
export function putEntry(owner: PricingOwner, input: NewEntry): PutStatus {
  const book = bookOf(owner);
  refresh(owner, book);
  const slot = input.slot;
  const entry: JournalEntry = {
    kind: input.kind,
    slot,
    target: input.target,
    owner,
    request: input.request,
    startedAt: new Date().toISOString(),
    attempts: 0,
    epoch: input.epoch,
    persisted: input.persist,
  };
  if (!input.persist) {
    if (book.ephemeral.has(slot)) return 'conflict';
    book.ephemeral.set(slot, entry);
    book.view = null;
    notify();
    return 'memory_only';
  }
  if (book.corrupt) return 'corrupt';
  if (book.persisted.has(slot)) return 'conflict';
  if (book.persisted.size >= MAX_JOURNAL_ENTRIES) return 'full';
  book.persisted.set(slot, entry);
  const stored = write(owner, book);
  book.view = null;
  notify();
  return stored ? 'stored' : 'memory_only';
}

/** Counts one more send of exactly this request. */
export function recordAttempt(owner: PricingOwner, slot: string, key: string, delta: 1 | -1): void {
  const book = bookOf(owner);
  const map = book.persisted.has(slot) ? book.persisted : book.ephemeral;
  const entry = map.get(slot);
  if (!entry || entry.request.key !== key) return;
  map.set(slot, { ...entry, attempts: Math.max(entry.attempts + delta, 0) });
  if (map === book.persisted) write(owner, book);
  book.view = null;
  notify();
}

/** Compare-and-delete: only the entry that still holds `key` is removed. */
export function removeEntry(owner: PricingOwner, slot: string, key: string): boolean {
  const book = bookOf(owner);
  const map = book.persisted.has(slot) ? book.persisted : book.ephemeral;
  const entry = map.get(slot);
  if (!entry || entry.request.key !== key) return false;
  map.delete(slot);
  if (map === book.persisted) write(owner, book);
  book.view = null;
  notify();
  return true;
}

/** The user knowingly discarded a damaged record: its content was never used. */
export function dropCorruptJournal(owner: PricingOwner): void {
  const book = bookOf(owner);
  refresh(owner, book);
  if (!book.corrupt) return;
  try {
    window.sessionStorage.removeItem(addressOf(owner));
    book.lastRaw = null;
  } catch {
    // Still unreadable: the notice stays.
    return;
  }
  book.corrupt = false;
  book.persisted = new Map();
  book.view = null;
  notify();
}

/** Marks a request as on its way. False when another request of the slot already is. */
export function beginFlight(owner: PricingOwner, slot: string, key: string): boolean {
  const book = bookOf(owner);
  if (book.inFlight.has(slot)) return false;
  book.inFlight.set(slot, key);
  book.view = null;
  notify();
  return true;
}

export function endFlight(owner: PricingOwner, slot: string, key: string): void {
  const book = bookOf(owner);
  if (book.inFlight.get(slot) !== key) return;
  book.inFlight.delete(slot);
  book.view = null;
  notify();
}

/** Test support: forgets every book and every stored document. */
export function resetPricingJournalForTests(): void {
  books.clear();
  try {
    const stale: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(PREFIX)) stale.push(key);
    }
    for (const key of stale) window.sessionStorage.removeItem(key);
  } catch {
    // Nothing stored, nothing to remove.
  }
  notify();
}

/** Test support: a page reload. The memory of the page is gone, the documents in `sessionStorage` stay. */
export function resetPricingJournalMemoryForReload(): void {
  books.clear();
  notify();
}
